import * as THREE from "three";
import { Engine } from "./core/engine";
import { Input } from "./core/input";
import {
  SAVE_VERSION,
  restoreInventory,
  snapshotInventory,
  writeSave,
  type SaveData,
} from "./core/save";
import { MAX_AIR, MAX_HEALTH, PLAYER_HEIGHT, Player, SNEAK_HEIGHT } from "./player/player";
import { HOTBAR_SIZE, Inventory } from "./player/inventory";
import { canHarvest, miningSpeed, requiredTool } from "./player/mining";
import { ChestBook, type ChestState } from "./player/chest";
import { FurnaceBook, type FurnaceState } from "./player/furnace";
import { World, CHUNK_X, type RaycastHit } from "./world/world";
import { createAtlas } from "./world/atlas";
import { BIOME_NAMES } from "./world/terrain";
import {
  AIR,
  BEDROCK,
  BLOCKS,
  CHEST,
  CRAFTING_TABLE,
  FURNACE,
  HOTBAR_BLOCKS,
  TNT,
  TORCH,
  WATER,
  blastResistance,
  blockName,
  isSolid,
} from "./world/blocks";
import { WORLD_HEIGHT } from "./world/chunk";
import {
  ARROW,
  attackDamage,
  BOW,
  CREATIVE_ITEMS,
  damageStack,
  foodItemInfo,
  isArmor,
  isBlockItem,
  isFood,
  isSword,
  isTool,
  itemName,
  makeStack,
  stackLimit,
  type ItemStack,
} from "./world/items";
import { DroppedItems } from "./entities/dropped-items";
import { Animals, animalDef, type Animal } from "./entities/animals";
import { Hostiles, HOSTILE_DEFS, type Hostile } from "./entities/hostiles";
import {
  PrimedTntSimulation,
  TNT_CHAIN_MIN_FUSE,
  TNT_CHAIN_MAX_FUSE,
} from "./entities/tnt";
import { Projectiles } from "./entities/projectiles";
import { ItemEntityRenderer } from "./render/item-entities";
import { AnimalRenderer } from "./render/animals";
import { HostileRenderer } from "./render/hostiles";
import { PrimedTntRenderer } from "./render/tnt";
import { ProjectileRenderer } from "./render/projectiles";
import { PlayerRenderer } from "./render/player";
import { MODE_LABELS, type GameMode } from "./core/mode";
import { Hud } from "./ui/hud";
import { provideAtlasTiles } from "./ui/icons";
import type { PickerItem } from "./ui/picker";
import { sound } from "./audio/sound-engine";
import { settingsManager, type GameSettings } from "./ui/settings";

/** How far the player can reach, in blocks. */
const REACH = 6;
/** Minimum seconds between block breaks in creative, so holding isn't instant. */
const BREAK_COOLDOWN = 0.18;
/** Minimum seconds between melee swings. */
const ATTACK_COOLDOWN = 0.45;
/** Where the player starts. */
const SPAWN_X = 0;
const SPAWN_Z = 0;
/** Autosave cadence while a world is running. */
const AUTOSAVE_MS = 30_000;

/** Everything the title screen hands over when the player starts a world. */
export interface GameOptions {
  /** World seed. Defaults to a random one. */
  seed?: number;
  /** Rules to start with. Defaults to creative. */
  mode?: GameMode;
  /** Chunk radius, clamped to 2–24. Falls back to the World default. */
  renderDistance?: number;
  /** Restore a saved world instead of generating a fresh one. */
  savedGame?: SaveData;
  /** Called when the player picks "Back to Main Menu" on the pause screen. */
  onExit?: () => void;
  /** Called when the player picks "Options..." on the pause screen. */
  onOptions?: () => void;
}

interface MiningTarget {
  x: number;
  y: number;
  z: number;
  progress: number;
}

export class Game {
  private readonly engine: Engine;
  private readonly input: Input;
  private readonly world: World;
  private readonly player: Player;
  private readonly hud: Hud;
  private readonly outline: THREE.LineSegments;
  /** Creative and survival keep separate packs so toggling modes never transfers items. */
  private readonly creativeInventory = new Inventory();
  private readonly survivalInventory = new Inventory();
  private inventory: Inventory = this.creativeInventory;
  /** Furnace contents and burn state, keyed by block position. */
  private readonly furnaces = new FurnaceBook();
  /** Chest contents, keyed by block position. */
  private readonly chests = new ChestBook();
  /** Physical drops in the world, independent of the active pack. */
  private readonly droppedItems: DroppedItems;
  private readonly itemRenderer: ItemEntityRenderer;
  /** Passive animals and their blocky models. */
  private readonly animals: Animals;
  private readonly animalRenderer: AnimalRenderer;
  /** Night-only hostile mobs, session-only and never written to the save. */
  private readonly hostiles: Hostiles;
  private readonly hostileRenderer: HostileRenderer;
  /** Primed TNT explosive entity simulation and renderer. */
  private readonly tntSimulation: PrimedTntSimulation;
  private readonly tntRenderer: PrimedTntRenderer;
  /** Active flying or stuck arrows. */
  private readonly projectiles: Projectiles;
  private readonly projectileRenderer: ProjectileRenderer;
  /** Player character renderer (1st-person viewmodel and 3rd-person Steve model). */
  private readonly playerRenderer: PlayerRenderer;
  /** Tension charge for the held bow (0 to 1.0). */
  private bowCharge = 0;
  /** Tracks the last tick's night state so dusk only announces itself once. */
  private wasNight = false;
  /** True while a full inventory has queued drops, so the toast fires once. */
  private overflowWarned = false;

  private readonly clock = new THREE.Clock();
  private frameTimes: number[] = [];
  private showDebug = false;
  private paused = false;
  /** True while waiting for the unlock event caused by opening a screen. */
  private interactionUnlock = false;
  private breakTimer = 0;
  private attackTimer = 0;
  private mode: GameMode = "creative";
  private dead = false;
  private spawnPoint = new THREE.Vector3();
  /** The block currently being mined in survival, if any. */
  private mining: MiningTarget | null = null;
  private digSoundTimer = 0;
  private running = false;
  private rafId = 0;
  private readonly onExit: (() => void) | null;

  /** Saves are armed only after start(), once the constructor has fully built the world. */
  private saveEnabled = false;
  private autosaveTimer = 0;
  private saveWarned = false;

  private readonly onOptions: (() => void) | null;
  private settingsUnsub: (() => void) | null = null;
  private lastRenderTimestamp = 0;

  constructor(container: HTMLElement, hudRoot: HTMLElement, options: GameOptions = {}) {
    const saved = options.savedGame ?? null;
    const seed = saved?.world.seed ?? options.seed ?? Math.floor(Math.random() * 2 ** 31);
    this.mode = saved?.world.mode ?? options.mode ?? "creative";
    this.onExit = options.onExit ?? null;
    this.onOptions = options.onOptions ?? null;

    this.engine = new Engine(container, createAtlas());
    let input: Input | null = null;
    try {
      input = new Input(this.engine.canvas);
      this.input = input;

      this.world = new World(seed);
      // Edits must be installed before the first chunk generates, both around
      // the saved position and anywhere the player later travels.
      if (saved) this.world.applySavedEdits(saved.blocks);
      // Lets players scale the world to their machine. Clamped so the streaming
      // invariants (mesh disc inside the generate square) always hold.
      const renderDistance = saved ? saved.world.renderDistance : options.renderDistance;
      if (renderDistance !== undefined) {
        this.world.renderDistance = clampInt(renderDistance, 2, 24);
      }
      this.world.setMesher(this.engine.createMesher(this.world.object3D));
      this.engine.scene.add(this.world.object3D);
      this.engine.setRenderDistance(this.world.renderDistance, CHUNK_X);

      this.player = new Player(this.world, this.engine.camera);

      provideAtlasTiles(this.engine.atlas.tiles);
      this.droppedItems = new DroppedItems(this.world, seed);
      this.itemRenderer = new ItemEntityRenderer(this.engine.scene, this.engine.atlas);
      this.animals = new Animals(this.world, seed + 0x51ed270b);
      this.animalRenderer = new AnimalRenderer(this.engine.scene);
      this.hostiles = new Hostiles(this.world, seed + 0x2f6e2b1);
      this.hostileRenderer = new HostileRenderer(this.engine.scene);
      this.tntSimulation = new PrimedTntSimulation(this.world);
      this.tntRenderer = new PrimedTntRenderer(this.engine.scene, this.engine.atlas);
      this.projectiles = new Projectiles(this.world, this.animals, this.hostiles);
      this.projectileRenderer = new ProjectileRenderer(this.engine.scene);
      this.playerRenderer = new PlayerRenderer(this.engine.scene, this.engine.camera, this.engine.atlas);
      this.hud = new Hud(hudRoot);
      this.initInventories();
      if (saved) {
        restoreInventory(this.creativeInventory, saved.inventories.creative);
        restoreInventory(this.survivalInventory, saved.inventories.survival);
      }

      // Block selection outline.
      const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(1.003, 1.003, 1.003));
      this.outline = new THREE.LineSegments(edges, this.engine.outlineMaterial);
      this.outline.visible = false;
      this.outline.renderOrder = 2;
      this.engine.scene.add(this.outline);

      // Build the starting area up front so we never drop the player into a
      // void. A saved game resumes at its recorded position and spawn point.
      if (saved) {
        this.spawnPoint.set(saved.spawn.x, saved.spawn.y, saved.spawn.z);
        const position = new THREE.Vector3(saved.player.x, saved.player.y, saved.player.z);
        this.player.yaw = saved.player.yaw;
        this.player.pitch = saved.player.pitch;
        this.world.update(position, 5000);
        this.player.spawn(position);
      } else {
        this.spawnPoint = this.world.findSpawn(SPAWN_X, SPAWN_Z);
        this.world.update(this.spawnPoint, 5000);
        this.player.spawn(this.spawnPoint);
      }

      this.wireHud();
      this.settingsUnsub = settingsManager.subscribe((s) => this.applySettings(s));
      this.applyMode(false);
      if (saved) {
        // applyMode resets vitals and the survival flight flag, so restore the
        // saved values only after it has established the mode rules.
        this.player.health = Math.max(1, Math.min(MAX_HEALTH, saved.player.health));
        this.player.air = Math.max(0, Math.min(MAX_AIR, saved.player.air));
        if (saved.player.food !== undefined) {
          this.player.foodStats.foodLevel = saved.player.food;
        }
        if (saved.player.foodSaturation !== undefined) {
          this.player.foodStats.foodSaturationLevel = saved.player.foodSaturation;
        }
        if (saved.player.foodExhaustion !== undefined) {
          this.player.foodStats.foodExhaustionLevel = saved.player.foodExhaustion;
        }
        this.player.flying = saved.player.flying && this.mode === "creative";
        this.hud.select(saved.player.selectedSlot);
        this.engine.sky.time = saved.world.skyTime;
        this.chests.restore(saved.chests);
        this.furnaces.restore(saved.furnaces);
        this.animals.restore(saved.animals);
      }
      this.wasNight = this.engine.sky.isNight;

      this.input.onLockChange = (locked) => {
        if (locked) {
          // A screen needs an unlocked cursor. A relock requested while
          // closing an earlier screen can complete after another screen has
          // opened; release it again instead of resuming behind the UI.
          if (this.hud.pickerOpen || this.hud.inventoryOpen) {
            this.interactionUnlock = true;
            document.exitPointerLock();
            return;
          }
          this.interactionUnlock = false;
          this.paused = false;
          this.hud.setOverlay(false, this.overlayHint());
          return;
        }
        // Opening a screen releases the mouse. That is not a pause; the screen
        // itself already paused the simulation, and closing it resumes play.
        if (this.interactionUnlock) {
          this.interactionUnlock = false;
          return;
        }
        this.paused = true;
        const pauseVisible =
          !this.hud.pickerOpen && !this.hud.inventoryOpen && !this.dead;
        this.hud.setOverlay(pauseVisible, this.overlayHint());
        if (pauseVisible) this.saveNow();
      };
      // A newly built world begins in play, not on the pause screen. The
      // browser may refuse to capture the mouse without a fresh gesture, so
      // the first canvas click asks again instead of blocking the view.
      this.hud.setOverlay(false, this.overlayHint());

      this.engine.canvas.addEventListener("click", this.onCanvasClick);
      window.addEventListener("keydown", this.onWindowKeyDown);
      document.addEventListener("visibilitychange", this.onVisibilityChange);

      // Handy for poking at the world from the devtools console.
      (window as unknown as Record<string, unknown>).game = this;
    } catch (err) {
      // A world that failed to finish building must not strand a live WebGL
      // context; the browser would refuse the next start.
      input?.dispose();
      this.engine.dispose();
      throw err;
    }
  }

  private readonly onWindowKeyDown = (e: KeyboardEvent): void => {
    sound.init();
    this.onKeyDown(e);
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") this.saveNow();
  };

  private readonly onCanvasClick = (): void => {
    sound.init();
    if (!this.input.locked) this.input.requestLock();
  };

  private wireHud(): void {
    this.hud.picker.onPick = (id) => this.pickBlock(id);
    this.hud.picker.onClose = () => this.closePicker(true);
    this.hud.inventoryScreen.onClose = () => this.closeInventory(true);
    this.hud.inventoryScreen.onChange = () => this.syncHotbar();
    this.hud.onRespawn = () => this.respawn();
    this.hud.onSwitchCreative = () => {
      this.dead = false;
      this.hud.hideDeath();
      this.setMode("creative", true);
      this.input.requestLock();
    };
    this.hud.onResume = () => this.input.requestLock();
    this.hud.onOptions = () => this.onOptions?.();
    this.hud.onQuitToMenu = () => this.onExit?.();
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.code === "F3") {
      if (e.repeat) return;
      this.showDebug = !this.showDebug;
      this.hud.setDebugVisible(this.showDebug);
      return;
    }
    if (e.repeat) return;

    if (e.code === "KeyB" && !this.dead) {
      const muted = sound.toggleMute();
      this.hud.toast(muted ? "Sound: Muted" : "Sound: Enabled");
      return;
    }
    if (e.code === "KeyM" && !this.dead) {
      this.setMode(this.mode === "creative" ? "survival" : "creative", true);
      return;
    }
    if ((e.code === "F5" || e.code === "KeyV") && !this.dead && !this.paused) {
      e.preventDefault();
      const mode = this.player.cycleCameraMode();
      this.hud.setCrosshairVisible(mode === "first-person");
      const label =
        mode === "first-person"
          ? "First person"
          : mode === "third-person-back"
            ? "Third person (back)"
            : "Third person (front)";
      this.hud.toast(label);
      return;
    }
    if (e.code === "KeyC" && !this.dead && !this.paused) {
      const skin = this.player.cycleSkin();
      const current = skin === "steve" ? "Steve" : "Alex";
      const other = skin === "steve" ? "Alex" : "Steve";
      this.hud.toast(`Playing as ${current} · Companion: ${other}`);
      return;
    }
    if (this.dead) return;

    if (e.code === "KeyE") {
      if (this.hud.pickerOpen) this.closePicker(true);
      else if (this.hud.inventoryOpen) this.closeInventory(true);
      else if (this.input.locked) {
        if (this.mode === "survival") this.openInventory();
        else this.openPicker();
      }
      return;
    }
    if (e.code === "Escape" && (this.hud.pickerOpen || this.hud.inventoryOpen)) {
      // Closing a screen returns to live play. Esc must not turn into a pause:
      // pointer lock may be refused after the screen's earlier unlock, and the
      // player can click the world to capture the mouse again.
      if (this.hud.pickerOpen) this.closePicker(true);
      else this.closeInventory(true);
      return;
    }
    if (e.code === "BracketLeft") {
      this.hud.scroll(-1);
      return;
    }
    if (e.code === "BracketRight") {
      this.hud.scroll(1);
      return;
    }

    // 1-9 pick the nine hotbar slots. Digit and numpad both.
    const digit = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
    if (digit && this.input.locked) {
      const n = Number(digit[1]);
      if (n >= 1 && n <= 9) this.hud.select(n - 1);
    }
  }

  /**
   * Write the current world to the single local save slot. Dead players are
   * skipped so quitting on the death screen keeps the last living state.
   */
  saveNow(): boolean {
    if (!this.saveEnabled || this.player.isDead) return false;
    const ok = writeSave(this.buildSave());
    if (ok) {
      this.saveWarned = false;
    } else if (!this.saveWarned) {
      this.saveWarned = true;
      this.hud.toast("Could not save — browser storage is unavailable or full");
    }
    return ok;
  }

  private buildSave(): SaveData {
    const position = this.player.position;
    return {
      version: SAVE_VERSION,
      savedAt: Date.now(),
      world: {
        seed: this.world.seed,
        mode: this.mode,
        renderDistance: this.world.renderDistance,
        skyTime: this.engine.sky.time,
      },
      player: {
        x: position.x,
        y: position.y,
        z: position.z,
        yaw: this.player.yaw,
        pitch: this.player.pitch,
        health: this.player.health,
        air: this.player.air,
        flying: this.player.flying,
        selectedSlot: this.hud.selected,
        food: this.player.foodStats.foodLevel,
        foodSaturation: this.player.foodStats.foodSaturationLevel,
        foodExhaustion: this.player.foodStats.foodExhaustionLevel,
      },
      spawn: {
        x: this.spawnPoint.x,
        y: this.spawnPoint.y,
        z: this.spawnPoint.z,
      },
      inventories: {
        creative: snapshotInventory(this.creativeInventory),
        survival: snapshotInventory(this.survivalInventory),
      },
      blocks: this.world.getEditSnapshot(),
      chests: this.chests.snapshot(),
      furnaces: this.furnaces.snapshot(),
      animals: this.animals.snapshot(),
    };
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    // The player just asked to start a world. Try to capture the mouse while
    // that click's activation may still be valid; if the browser refuses, the
    // world still runs and clicking the canvas retries the capture.
    this.input.requestLock();
    this.saveEnabled = true;
    // Put a save in place immediately, so even a first-session crash has a
    // world to Continue from and a new world replaces the old slot right away.
    this.saveNow();
    this.autosaveTimer = window.setInterval(() => this.saveNow(), AUTOSAVE_MS);
    this.rendererLoop();
  }

  /** Stop the loop and free the renderer so the menu can start a fresh game. */
  dispose(): void {
    if (this.saveEnabled) {
      this.saveNow();
      this.saveEnabled = false;
    }
    this.running = false;
    if (this.autosaveTimer !== 0) {
      window.clearInterval(this.autosaveTimer);
      this.autosaveTimer = 0;
    }
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("keydown", this.onWindowKeyDown);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.engine.canvas.removeEventListener("click", this.onCanvasClick);
    this.input.dispose();
    this.furnaces.clear();
    this.chests.clear();
    this.droppedItems.clear();
    this.animals.clear();
    this.hostiles.clear();
    this.tntSimulation.clear();
    this.projectiles.clear();
    this.itemRenderer.dispose();
    this.animalRenderer.dispose();
    this.hostileRenderer.dispose();
    this.tntRenderer.dispose();
    this.projectileRenderer.dispose();
    this.playerRenderer.dispose();
    this.engine.scene.remove(this.outline);
    this.outline.geometry.dispose();
    this.engine.dispose();
    this.settingsUnsub?.();
    const w = window as unknown as Record<string, unknown>;
    if (w.game === this) delete w.game;
  }

  applySettings(s: GameSettings): void {
    this.world.renderDistance = clampInt(s.renderDistance, 2, 24);
    this.engine.setRenderDistance(this.world.renderDistance, CHUNK_X);
    this.engine.setGraphicsQuality(s.graphicsQuality);
    this.engine.setFov(s.fov);
    this.player.mouseSensitivity = s.mouseSensitivity;
    this.player.baseFov = s.fov;
    this.player.fovEffects = s.fovEffects;
    this.playerRenderer.viewBobbing = s.viewBobbing;
  }

  private rendererLoop = (timestamp = 0): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.rendererLoop);

    const maxFps = settingsManager.settings.maxFps;
    if (maxFps > 0) {
      const minInterval = 1000 / maxFps;
      if (timestamp - this.lastRenderTimestamp < minInterval - 1.5) {
        return;
      }
    }
    this.lastRenderTimestamp = timestamp;

    const dt = Math.min(this.clock.getDelta(), 0.1);

    this.tick(dt);
    this.engine.render();
    this.input.endFrame();
  };

  private tick(dt: number): void {
    const frameStart = performance.now();

    if (!this.paused && !this.player.isDead) {
      const held = this.inventory.slots[this.hud.selected] ?? null;
      this.player.blocking =
        held !== null && isSword(held.id) && this.input.locked && this.input.mouse.right;
      this.player.update(this.input, dt);
      if (this.input.mouse.wheel !== 0) this.hud.scroll(this.input.mouse.wheel);
      this.breakTimer = Math.max(0, this.breakTimer - dt);
      this.attackTimer = Math.max(0, this.attackTimer - dt);
    } else {
      this.player.blocking = false;
    }

    // Furnaces keep working while their screen is open (which pauses movement)
    // and while the player walks around the world.
    this.furnaces.update(dt);
    if (this.hud.furnaceOpen) this.hud.refreshFurnace();

    if (this.player.isDead && !this.dead) this.onDeath();

    this.engine.sky.update(dt, this.player.position);
    const sky = this.engine.sky.sample();
    const night = this.engine.sky.isNight;

    if (
      night &&
      !this.wasNight &&
      !this.paused &&
      !this.player.isDead &&
      this.mode === "survival"
    ) {
      this.hud.toast("Night falls — zombies are roaming");
    }
    this.wasNight = night;

    // One scalar tint drives the whole world's day/night brightness.
    this.engine.solidMaterial.color.setScalar(sky.light);
    this.engine.waterMaterial.color.setScalar(sky.light);
    this.engine.outlineMaterial.opacity = 0.3 + 0.3 * sky.light;

    const fog = this.engine.scene.fog as THREE.Fog;
    fog.color.copy(this.engine.sky.fogColorFor(this.player.headInWater));
    this.engine.renderer.setClearColor(fog.color, 1);

    this.world.update(this.player.position, 6);
    this.updateTargeting(dt);

    // Drops and animals only age and move during active play. Their visuals
    // still track the simulation while paused, so nothing pops in or out.
    if (!this.paused && !this.player.isDead) {
      const collected = this.droppedItems.update(dt, this.player.position);
      if (collected.length > 0) {
        sound.playItemPickup();
        for (const stack of collected) this.inventory.addStack(stack);
        this.syncHotbar();
      }
      this.animals.update(dt, this.player.position);

      const attacks = this.hostiles.update(dt, this.player.position, {
        night,
        allowSpawn: this.mode === "survival",
      });
      for (const attack of attacks) {
        const armorDef = this.inventory.totalArmorDefense;
        this.player.hurt(
          attack.damage,
          attack.hostile.kind === "skeleton" ? "was shot by a Skeleton" : "was slain by a Zombie",
          armorDef,
        );
        const broke = this.inventory.damageArmor(attack.damage);
        if (broke) {
          sound.playToolBreak();
          this.hud.toast("Armor broke!");
        }
      }

      // Collect skeleton shots into projectile simulation
      const hostileShots = this.hostiles.takePendingShots();
      for (const shot of hostileShots) {
        this.projectiles.spawnArrow(
          shot.origin,
          shot.direction,
          22,
          shot.damage,
          "skeleton",
          false,
        );
      }

      // Creeper hissing fuse sound events
      const creeperFuseEvents = this.hostiles.takePendingFuseSounds();
      for (const ev of creeperFuseEvents) {
        const { pan, distanceFactor } = this.spatialSound(ev.position, 24);
        sound.playCreeperFuse(pan, distanceFactor);
      }

      // Creeper detonations
      const creeperExplosions = this.hostiles.takePendingExplosions();
      for (const expl of creeperExplosions) {
        this.triggerExplosion(expl.position, expl.power, "creeper");
      }

      // Primed TNT updates & detonations
      const tntExplosions = this.tntSimulation.update(dt);
      for (const expl of tntExplosions) {
        this.triggerExplosion(expl.position, expl.power, "tnt");
      }

      // Update projectile flight and collisions
      const projResult = this.projectiles.update(dt, this.player.position, this.mode === "survival");
      if (projResult.playerDamage > 0) {
        const armorDef = this.inventory.totalArmorDefense;
        this.player.hurt(projResult.playerDamage, "was shot by a Skeleton", armorDef);
        const broke = this.inventory.damageArmor(projResult.playerDamage);
        if (broke) {
          sound.playToolBreak();
          this.hud.toast("Armor broke!");
        }
      }
      if (projResult.collectedArrows > 0) {
        sound.playItemPickup();
        this.inventory.add(ARROW, projResult.collectedArrows);
        this.syncHotbar();
        this.hud.toast(`Picked up ${projResult.collectedArrows} Arrow`);
      }
    }
    this.itemRenderer.sync(this.droppedItems.items, sky.light);
    this.animalRenderer.sync(this.animals.animals, sky.light);
    this.hostileRenderer.sync(this.hostiles.hostiles, sky.light);
    this.tntRenderer.sync(this.tntSimulation.tnts, sky.light);
    this.projectileRenderer.sync(this.projectiles.activeArrows, sky.light);

    const isSwinging =
      this.mining !== null ||
      this.breakTimer > 0 ||
      this.attackTimer > 0 ||
      (this.input.locked && this.input.mouse.left);
    const held = this.inventory.slots[this.hud.selected] ?? null;
    this.playerRenderer.sync(
      this.player,
      sky.light,
      dt,
      held,
      isSwinging,
      this.bowCharge,
      this.inventory.armor,
    );

    this.hud.setUnderwater(this.player.headInWater);
    if (this.mode === "survival") {
      this.hud.setVitals(
        this.player.health,
        MAX_HEALTH,
        this.player.air,
        MAX_AIR,
        this.player.headInWater,
        this.player.foodStats.foodLevel,
        this.player.foodStats.foodSaturationLevel,
      );
      this.hud.setArmor(this.inventory.totalArmorDefense);
    } else {
      this.hud.setArmor(0);
    }
    this.hud.setDamageFlash(this.player.hurtFlash);

    if (this.showDebug) this.updateDebug(dt, frameStart, sky.light);
  }

  // --- game modes ---------------------------------------------------------

  private overlayHint(): string {
    return this.mode === "survival"
      ? "Click to play · Survival: mine, hunt, cook, and survive the night"
      : "Click to play · Creative: unlimited blocks and flight";
  }

  /** Push the current mode's rules onto the player and the HUD. */
  private applyMode(announce: boolean): void {
    const survival = this.mode === "survival";
    this.inventory = survival ? this.survivalInventory : this.creativeInventory;
    this.overflowWarned = false;
    this.player.survival = survival;
    this.player.allowFlight = !survival;
    if (survival) this.player.flying = false;
    this.player.health = MAX_HEALTH;
    this.player.air = MAX_AIR;
    this.player.hurtFlash = 0;
    if (!survival) this.hostiles.clear();

    this.dead = false;
    this.hud.hideDeath();
    this.hud.setMode(this.mode);
    this.hud.setDamageFlash(0);
    this.resetMining();
    this.attackTimer = 0;
    this.syncHotbar();

    if (announce) {
      this.hud.toast(
        survival
          ? "Survival mode — mine blocks, then pick up the drops; don't fall too far"
          : "Creative mode — unlimited blocks, double-tap Space to fly",
      );
    }
  }

  private setMode(mode: GameMode, announce: boolean): void {
    // Clicking the active mode button must not silently heal the player.
    if (this.mode === mode) return;
    if (this.hud.inventoryOpen) this.closeInventory(false);
    if (this.hud.pickerOpen) this.closePicker(false);
    this.mode = mode;
    this.applyMode(announce);

    if (announce) {
      // Keep the URL shareable across reloads.
      const url = new URL(location.href);
      url.searchParams.set("mode", mode);
      history.replaceState(null, "", url);
      this.hud.setOverlay(this.paused, this.overlayHint());
    }
  }

  /** Build both packs: the creative palette and an empty survival inventory. */
  private initInventories(): void {
    this.creativeInventory.clear();
    for (let i = 0; i < HOTBAR_SIZE && i < HOTBAR_BLOCKS.length; i++) {
      this.creativeInventory.setSlot(i, { id: HOTBAR_BLOCKS[i], count: 1 });
    }
    this.survivalInventory.clear();
    this.inventory = this.mode === "survival" ? this.survivalInventory : this.creativeInventory;
  }

  /** Push the inventory onto the HUD: counts in survival, plain icons in creative. */
  private syncHotbar(): void {
    this.hud.setHotbar(this.inventory.hotbar(), this.mode === "survival");
    if (this.hud.inventoryOpen) this.hud.inventoryScreen.render();
    this.warnOverflow();
  }

  /** Tell the player once when a full pack parks drops in the overflow. */
  private warnOverflow(): void {
    const full = this.inventory.overflow.length > 0;
    if (full && !this.overflowWarned) {
      this.hud.toast("Inventory full — extra items are held until a slot frees");
    }
    this.overflowWarned = full;
  }

  // --- picker --------------------------------------------------------------

  private pickerItems(): PickerItem[] {
    const items: PickerItem[] = [];
    for (const def of BLOCKS) {
      if (def.placeable) items.push({ id: def.id, count: null });
    }
    for (const id of CREATIVE_ITEMS) {
      items.push({ id, count: null });
    }
    return items;
  }

  private openPicker(): void {
    this.hud.openPicker("Creative Inventory", this.pickerItems());
    this.resetMining();
    this.paused = true;
    this.exitLockForInteraction();
    this.hud.setOverlay(false, this.overlayHint());
  }

  private closePicker(relock: boolean): void {
    this.hud.closePicker();
    this.resumeFromInteraction(relock);
  }

  private openInventory(): void {
    this.openInventoryScreen("inventory");
  }

  private openCraftingTable(): void {
    this.openInventoryScreen("table");
  }

  private openFurnace(x: number, y: number, z: number): void {
    if (this.world.getBlock(x, y, z) !== FURNACE) return;
    this.openInventoryScreen("furnace", this.furnaces.at(x, y, z));
  }

  private openChest(x: number, y: number, z: number): void {
    if (this.world.getBlock(x, y, z) !== CHEST) return;
    this.openInventoryScreen("chest", null, this.chests.at(x, y, z));
  }

  private openInventoryScreen(
    mode: "inventory" | "table" | "furnace" | "chest",
    furnace: FurnaceState | null = null,
    chest: ChestState | null = null,
  ): void {
    this.hud.openInventory(this.inventory, mode, furnace, chest);
    this.resetMining();
    this.paused = true;
    this.exitLockForInteraction();
    this.hud.setOverlay(false, this.overlayHint());
  }

  /** Leave pointer lock for a screen, ignoring the unlock event it produces. */
  private exitLockForInteraction(): void {
    if (!document.pointerLockElement) return;
    this.interactionUnlock = true;
    document.exitPointerLock();
  }

  private closeInventory(relock: boolean): void {
    this.hud.closeInventory();
    this.resumeFromInteraction(relock);
    this.syncHotbar();
  }

  /**
   * Leave an inventory/picker screen and return to the world. This must never
   * show the pause overlay: the player closed a screen, not paused the game.
   * The lock request is best-effort, so a browser refusal leaves the world
   * running with an uncaptured cursor instead of freezing behind a menu.
   */
  private resumeFromInteraction(relock: boolean): void {
    this.paused = false;
    this.hud.setOverlay(false, this.overlayHint());
    if (relock) this.input.requestLock();
  }

  private pickBlock(id: number): void {
    this.inventory.setSlot(this.hud.selected, makeStack(id, stackLimit(id)));
    this.closePicker(true);
    this.syncHotbar();
  }

  /** Middle click: put whatever you are looking at into the active slot. */
  private pickLookingAt(hit: RaycastHit): void {
    const id = hit.block;
    const slot = this.inventory.hotbarIndexOf(id);
    if (slot >= 0) {
      this.hud.select(slot);
      return;
    }
    if (this.mode === "survival") {
      this.hud.toast(`No ${blockName(id)} in your inventory`);
      return;
    }
    this.inventory.setSlot(this.hud.selected, { id, count: 1 });
    this.syncHotbar();
  }

  // --- death / respawn -----------------------------------------------------

  private onDeath(): void {
    this.dead = true;
    this.player.velocity.set(0, 0, 0);
    this.resetMining();
    this.hud.showDeath(this.player.lastCause);
    this.hud.toast("You died");
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private respawn(): void {
    this.player.respawn(this.spawnPoint);
    this.dead = false;
    this.hud.hideDeath();
    this.world.update(this.player.position, 8);
    this.input.requestLock();
  }

  // --- targeting / block editing -------------------------------------------

  private eye(): THREE.Vector3 {
    const eye = this.player.position.clone();
    eye.y += this.player.eyeHeight;
    return eye;
  }

  private currentHit(): RaycastHit | null {
    return this.world.raycast(this.eye(), this.player.forward, REACH);
  }

  private updateTargeting(dt: number): void {
    if (this.paused || this.player.isDead) {
      this.outline.visible = false;
      this.resetMining();
      this.bowCharge = 0;
      return;
    }

    if (this.updateBow(dt)) {
      this.outline.visible = false;
      this.resetMining();
      return;
    }

    const eye = this.eye();
    const hit = this.world.raycast(eye, this.player.forward, REACH);
    const animalHit = this.animals.raycast(eye, this.player.forward, REACH);
    const hostileHit = this.hostiles.raycast(eye, this.player.forward, REACH);
    // The nearest body wins; a cow or zombie behind a wall does not.
    let animal: Animal | null = null;
    let hostile: Hostile | null = null;
    let targetDistance = hit?.distance ?? Infinity;
    if (animalHit && animalHit.distance < targetDistance) {
      animal = animalHit.animal;
      targetDistance = animalHit.distance;
    }
    if (hostileHit && hostileHit.distance < targetDistance) {
      animal = null;
      hostile = hostileHit.hostile;
      targetDistance = hostileHit.distance;
    }

    if (animal || hostile) {
      this.outline.visible = false;
      this.resetMining();
      if (this.input.wasLeftClicked() && this.attackTimer <= 0) {
        if (animal) this.attackAnimal(animal);
        else if (hostile) this.attackHostile(hostile);
      }
      if (this.input.wasRightClicked() && (this.tryEatSelected() || this.tryEquipSelectedArmor())) return;
      return;
    }

    if (!hit) {
      this.outline.visible = false;
      if (this.input.wasRightClicked() && (this.tryEatSelected() || this.tryEquipSelectedArmor())) return;
      this.resetMining();
      return;
    }

    this.outline.visible = true;
    if (hit.block === TORCH) {
      this.outline.scale.set(0.252, 0.628, 0.252);
      this.outline.position.set(hit.x + 0.5, hit.y + 0.3125, hit.z + 0.5);
    } else {
      this.outline.scale.set(1, 1, 1);
      this.outline.position.set(hit.x + 0.5, hit.y + 0.5, hit.z + 0.5);
    }

    if (this.input.wasMiddleClicked()) this.pickLookingAt(hit);

    // Right-clicking a crafting table, furnace or chest opens its screen.
    // Sneaking bypasses that so players can still build against them.
    if (this.input.wasRightClicked() && !this.player.sneaking) {
      if (hit.block === CRAFTING_TABLE) {
        this.openCraftingTable();
        return;
      }
      if (hit.block === FURNACE) {
        this.openFurnace(hit.x, hit.y, hit.z);
        return;
      }
      if (hit.block === CHEST) {
        this.openChest(hit.x, hit.y, hit.z);
        return;
      }
    }

    // Eating or equipping armor takes precedence over placing the selected stack.
    if (this.input.wasRightClicked() && (this.tryEatSelected() || this.tryEquipSelectedArmor())) return;

    if (this.mode === "creative") this.updateCreativeActions(hit);
    else this.updateSurvivalActions(hit, dt);
  }

  /** Swing at a mob with the selected item, spawn loot and apply durability wear. */
  private attackAnimal(animal: Animal): void {
    const held = this.inventory.slots[this.hud.selected] ?? null;
    const damage = this.mode === "creative" ? 100 : attackDamage(held?.id ?? 0);
    const kind = animal.kind;
    const position = animal.position.clone();
    sound.playPunchHit();
    const killed = this.animals.damage(animal, damage, this.player.position);
    this.attackTimer = ATTACK_COOLDOWN;

    if (this.mode === "survival") {
      this.player.foodStats.addExhaustion(0.3);
      if (held && isTool(held.id)) this.wearSelectedTool();
    }

    if (!killed) return;
    const drops = this.animals.rollDrops(kind);
    for (const stack of drops) {
      this.droppedItems.spawn(stack, position.x, position.y + 0.65, position.z);
    }
    if (drops.length > 0) {
      this.hud.toast(
        `${animalDef(kind).name} dropped ${drops.map((stack) => itemName(stack.id)).join(", ")}`,
      );
    }
  }

  /**
   * Swing at a hostile mob, roll loot on defeat and wear the selected tool.
   */
  private attackHostile(hostile: Hostile): void {
    const held = this.inventory.slots[this.hud.selected] ?? null;
    const damage = this.mode === "creative" ? 100 : attackDamage(held?.id ?? 0);
    const kind = hostile.kind;
    const position = hostile.position.clone();
    sound.playPunchHit();
    if (kind === "zombie") sound.playZombieGroan();
    else if (kind === "skeleton") sound.playSkeletonRattle();
    else if (kind === "spider") sound.playSpiderHiss();
    const killed = this.hostiles.damage(hostile, damage, this.player.position);
    this.attackTimer = ATTACK_COOLDOWN;
    if (this.mode === "survival") {
      this.player.foodStats.addExhaustion(0.3);
      if (held && isTool(held.id)) this.wearSelectedTool();
    }

    if (!killed) return;
    if (kind === "spider") sound.playSpiderDeath();
    const drops = this.hostiles.rollDrops(kind);
    for (const stack of drops) {
      this.droppedItems.spawn(stack, position.x, position.y + 0.65, position.z);
    }
    if (drops.length > 0) {
      this.hud.toast(
        `${HOSTILE_DEFS[kind].name} dropped ${drops.map((stack) => itemName(stack.id)).join(", ")}`,
      );
    }
  }

  /** Eat the selected food, if any. Returns true when the click was handled. */
  private tryEatSelected(): boolean {
    const stack = this.inventory.slots[this.hud.selected] ?? null;
    if (!stack || !isFood(stack.id)) return false;

    const food = foodItemInfo(stack.id);
    if (!food) return false;

    if (this.mode === "survival" && !this.player.foodStats.needFood() && !food.alwaysEdible) {
      this.hud.toast("You are not hungry");
      return true;
    }

    this.player.foodStats.addStats(food.foodLevel, food.saturationModifier);
    sound.playEat();
    sound.playBurp();

    if (this.mode === "survival") this.inventory.takeOne(this.hud.selected);
    this.syncHotbar();

    const shanks = food.foodLevel / 2;
    const label = Number.isInteger(shanks) ? String(shanks) : shanks.toFixed(1);
    this.hud.toast(`Ate ${itemName(stack.id)} (+${label} ${shanks === 1 ? "shank" : "shanks"})`);
    return true;
  }

  /** Equip the selected armor piece from hotbar. Returns true when equipped. */
  private tryEquipSelectedArmor(): boolean {
    const stack = this.inventory.slots[this.hud.selected] ?? null;
    if (!stack || !isArmor(stack.id)) return false;

    const equipped = this.inventory.equipArmorFromSlot(this.hud.selected);
    if (!equipped) return false;

    sound.playUiClick();
    this.syncHotbar();
    this.hud.setArmor(this.mode === "survival" ? this.inventory.totalArmorDefense : 0);
    this.hud.toast(`Equipped ${itemName(stack.id)}`);
    return true;
  }

  /**
   * Handle charging and firing the bow when held in the active hotbar slot.
   * Returns true while the bow is drawing or releasing an arrow, suppressing
   * block placing and mining.
   */
  private updateBow(dt: number): boolean {
    const held = this.inventory.slots[this.hud.selected] ?? null;
    if (!held || held.id !== BOW) {
      this.bowCharge = 0;
      return false;
    }

    const hasArrow = this.mode === "creative" || this.inventory.has(ARROW);

    if (this.input.mouse.right) {
      if (hasArrow) {
        this.bowCharge = Math.min(1.0, this.bowCharge + dt * 1.35);
      } else {
        if (this.input.wasRightClicked()) {
          this.hud.toast("No arrows in inventory!");
        }
        this.bowCharge = 0;
      }
      return true;
    }

    // Right-click released
    if (this.bowCharge > 0.15) {
      const charge = this.bowCharge;
      this.bowCharge = 0;
      const arrowSlot = this.inventory.firstSlotWith(ARROW);
      if (this.mode === "creative" || arrowSlot !== -1) {
        if (this.mode === "survival" && arrowSlot !== -1) {
          this.inventory.takeOne(arrowSlot);
          this.wearSelectedTool();
          this.syncHotbar();
        }
        const lookDir = new THREE.Vector3();
        this.engine.camera.getWorldDirection(lookDir);
        const origin = this.eye().addScaledVector(lookDir, 0.4);
        const speed = 14 + charge * 18; // 14 to 32 m/s
        const damage = Math.floor(3 + charge * 7); // 3 to 10 damage
        this.projectiles.spawnArrow(origin, lookDir, speed, damage, "player", true);
        sound.playBowShoot();
        const percent = Math.round(charge * 100);
        this.hud.toast(`Shot arrow (${percent}% power)`);
      }
      return true;
    }

    this.bowCharge = 0;
    return false;
  }

  /** Creative: instant break, unlimited building. */
  private updateCreativeActions(hit: RaycastHit): void {
    if (hit.block === TNT && this.input.wasRightClicked()) {
      this.primeTntBlock(hit.x, hit.y, hit.z);
      return;
    }
    if (this.input.mouse.left && this.breakTimer <= 0) {
      sound.playBlockBreak(hit.block);
      this.world.setBlock(hit.x, hit.y, hit.z, AIR);
      this.checkTorchAbove(hit.x, hit.y, hit.z);
      this.dropFurnaceContents(hit.x, hit.y, hit.z);
      this.dropChestContents(hit.x, hit.y, hit.z);
      this.breakTimer = BREAK_COOLDOWN;
      return;
    }
    if (this.input.mouse.right) this.placeAt(hit);
  }

  /** Survival: hold to mine, and every placement costs an inventory block. */
  private updateSurvivalActions(hit: RaycastHit, dt: number): void {
    if (hit.block === TNT) {
      if (this.input.mouse.left) {
        this.primeTntBlock(hit.x, hit.y, hit.z);
        this.breakTimer = BREAK_COOLDOWN;
        return;
      }
      if (this.input.wasRightClicked()) {
        const held = this.inventory.slots[this.hud.selected] ?? null;
        if (!held || !isBlockItem(held.id)) {
          this.primeTntBlock(hit.x, hit.y, hit.z);
          return;
        }
      }
    }

    if (this.input.mouse.right) this.placeAt(hit);

    const def = BLOCKS[hit.block];
    const mining = this.input.mouse.left && Number.isFinite(def.hardness);
    if (!mining) {
      this.resetMining();
      return;
    }

    const held = this.inventory.slots[this.hud.selected] ?? null;
    const heldId = held?.id ?? 0;

    if (
      !this.mining ||
      this.mining.x !== hit.x ||
      this.mining.y !== hit.y ||
      this.mining.z !== hit.z
    ) {
      this.mining = { x: hit.x, y: hit.y, z: hit.z, progress: 0 };
      this.digSoundTimer = 0;
    }

    this.digSoundTimer += dt;
    if (this.digSoundTimer >= 0.22) {
      this.digSoundTimer = 0;
      sound.playDig(hit.block);
    }

    this.mining.progress += (dt * miningSpeed(hit.block, heldId)) / def.hardness;
    this.hud.setBreakProgress(this.mining.progress);

    if (this.mining.progress >= 1) {
      sound.playBlockBreak(hit.block);
      this.world.setBlock(hit.x, hit.y, hit.z, AIR);
      this.checkTorchAbove(hit.x, hit.y, hit.z);
      this.dropFurnaceContents(hit.x, hit.y, hit.z);
      this.dropChestContents(hit.x, hit.y, hit.z);
      if (def.drop > 0) {
        if (canHarvest(hit.block, heldId)) {
          this.droppedItems.spawn(
            makeStack(def.drop),
            hit.x + 0.5,
            hit.y + 0.5,
            hit.z + 0.5,
          );
        } else {
          const needs = requiredTool(hit.block);
          if (needs) this.hud.toast(`${def.name} needs a ${needs}`);
        }
      }
      this.wearSelectedTool();
      this.player.foodStats.addExhaustion(0.025);
      this.resetMining();
    }
  }

  /** Remove a furnace's contents and spill them into the world. */
  private dropFurnaceContents(x: number, y: number, z: number): void {
    const furnace = this.furnaces.remove(x, y, z);
    if (!furnace) return;
    this.spillStacks(furnace.drain(), x, y, z);
  }

  /** Remove a chest's contents and spill them into the world. */
  private dropChestContents(x: number, y: number, z: number): void {
    const chest = this.chests.remove(x, y, z);
    if (!chest) return;
    this.spillStacks(chest.drain(), x, y, z);
  }

  /**
   * Survival drops container contents on the ground so they can be collected.
   * Creative keeps handing them straight back to the unlimited palette, which
   * avoids littering a mode where items have no cost.
   */
  private spillStacks(stacks: readonly ItemStack[], x: number, y: number, z: number): void {
    if (stacks.length === 0) return;
    if (this.mode === "survival") {
      for (const stack of stacks) {
        this.droppedItems.spawn(stack, x + 0.5, y + 0.5, z + 0.5);
      }
      return;
    }
    for (const stack of stacks) this.inventory.addStack(stack);
    this.syncHotbar();
  }

  /** Spend one use of the selected tool; remove it when it breaks. */
  private wearSelectedTool(): void {
    const index = this.hud.selected;
    const stack = this.inventory.slots[index] ?? null;
    if (!stack) return;

    const next = damageStack(stack);
    if (next === stack) return;

    if (next) {
      this.inventory.setSlot(index, next);
    } else {
      sound.playToolBreak();
      this.inventory.setSlot(index, null);
      this.hud.toast(`${itemName(stack.id)} broke`);
    }
    this.syncHotbar();
  }

  private resetMining(): void {
    this.mining = null;
    this.digSoundTimer = 0;
    this.hud.setBreakProgress(0);
  }

  private placeAt(hit: RaycastHit): void {
    const stack = this.inventory.slots[this.hud.selected] ?? null;
    if (!stack || !isBlockItem(stack.id)) return;

    const px = hit.x + hit.nx;
    const py = hit.y + hit.ny;
    const pz = hit.z + hit.nz;
    if (isSolid(stack.id) && this.overlapsPlayer(px, py, pz)) return;

    if (stack.id === TORCH) {
      if (hit.ny < 0 || !this.world.isSolidAt(px, py - 1, pz)) return;
    }

    const id = stack.id;
    const survival = this.mode === "survival";
    if (survival && !this.inventory.takeOne(this.hud.selected)) return;

    if (!this.world.setBlock(px, py, pz, id)) {
      // The world refused the edit (out of bounds, or the block was already
      // there); give the inventory block back so nothing is lost.
      if (survival) this.inventory.add(id);
      return;
    }
    sound.playBlockPlace(id);
    this.syncHotbar();
  }

  /** If a floor torch was sitting on top of this block, pop it off as a drop. */
  private checkTorchAbove(x: number, y: number, z: number): void {
    if (this.world.getBlock(x, y + 1, z) === TORCH) {
      this.world.setBlock(x, y + 1, z, AIR);
      if (this.mode === "survival") {
        this.droppedItems.spawn(makeStack(TORCH), x + 0.5, y + 1.5, z + 0.5);
      }
    }
  }

  /** Would a block at these coords intersect the player's hitbox? */
  private overlapsPlayer(x: number, y: number, z: number): boolean {
    const p = this.player.position;
    const half = 0.3;
    const height = this.player.sneaking ? SNEAK_HEIGHT : PLAYER_HEIGHT;
    return (
      x + 1 > p.x - half &&
      x < p.x + half &&
      z + 1 > p.z - half &&
      z < p.z + half &&
      y + 1 > p.y &&
      y < p.y + height
    );
  }

  /** Calculate stereo pan (-1 to 1) and distance attenuation factor (0 to 1) for spatial audio. */
  private spatialSound(pos: THREE.Vector3, maxRange = 36): { pan: number; distanceFactor: number } {
    const dx = pos.x - this.player.position.x;
    const dy = pos.y - this.player.position.y;
    const dz = pos.z - this.player.position.z;
    const dist = Math.hypot(dx, dy, dz);
    const distanceFactor = Math.max(0, 1.0 - dist / maxRange);
    if (dist < 1e-3) return { pan: 0, distanceFactor };

    const rightX = Math.cos(this.player.yaw);
    const rightZ = -Math.sin(this.player.yaw);
    const pan = Math.max(-1, Math.min(1, (dx / dist) * rightX + (dz / dist) * rightZ));
    return { pan, distanceFactor };
  }

  /** Turn a world TNT block into a primed falling/ticking TNT entity. */
  private primeTntBlock(x: number, y: number, z: number, fuse = 4.0): void {
    this.world.setBlock(x, y, z, AIR);
    this.checkTorchAbove(x, y, z);
    this.tntSimulation.spawn(x + 0.5, y, z + 0.5, fuse);
    const { pan, distanceFactor } = this.spatialSound(new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5), 24);
    sound.playTntPrime(pan, distanceFactor);
  }

  /**
   * Handle detonation blast radius for Creeper or TNT.
   * Includes blast resistance checks, block drops, chain reactions, submerged water dampening,
   * player armor defense reduction, and radial knockback.
   */
  private triggerExplosion(origin: THREE.Vector3, power: number, source: "creeper" | "tnt"): void {
    // 1. Play explosion sound with stereo panning and falloff
    const { pan, distanceFactor } = this.spatialSound(origin, 48);
    sound.playExplosion(pan, distanceFactor);

    // 2. Submerged explosion check: in Minecraft, water completely absorbs block destruction
    const originBlock = this.world.getBlock(
      Math.floor(origin.x),
      Math.floor(origin.y),
      Math.floor(origin.z),
    );
    const isSubmerged = originBlock === WATER;

    // 3. Block destruction (if not submerged)
    if (!isSubmerged) {
      const radius = Math.ceil(power * 1.35);
      const minX = Math.floor(origin.x - radius);
      const maxX = Math.floor(origin.x + radius);
      const minY = Math.max(0, Math.floor(origin.y - radius));
      const maxY = Math.min(WORLD_HEIGHT - 1, Math.floor(origin.y + radius));
      const minZ = Math.floor(origin.z - radius);
      const maxZ = Math.floor(origin.z + radius);

      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          for (let x = minX; x <= maxX; x++) {
            const dx = x + 0.5 - origin.x;
            const dy = y + 0.5 - origin.y;
            const dz = z + 0.5 - origin.z;
            const dist = Math.hypot(dx, dy, dz);
            if (dist > radius) continue;

            const block = this.world.getBlock(x, y, z);
            if (block === AIR || block === BEDROCK || block === WATER) continue;

            const resistance = blastResistance(block);
            const blastPower = (1.0 - dist / (power * 1.5)) * power;
            if (blastPower <= resistance / 12.0) continue;

            if (block === TNT) {
              // TNT chain reaction: prime nearby TNT blocks with shortened random fuse
              this.world.setBlock(x, y, z, AIR);
              this.checkTorchAbove(x, y, z);
              const chainFuse =
                TNT_CHAIN_MIN_FUSE +
                Math.random() * (TNT_CHAIN_MAX_FUSE - TNT_CHAIN_MIN_FUSE);
              const primed = this.tntSimulation.spawn(x + 0.5, y, z + 0.5, chainFuse);
              const dirLen = dist || 1;
              primed.velocity.x = (dx / dirLen) * (3.0 + Math.random() * 2.0);
              primed.velocity.y = 2.5 + Math.random() * 2.0;
              primed.velocity.z = (dz / dirLen) * (3.0 + Math.random() * 2.0);
            } else {
              this.world.setBlock(x, y, z, AIR);
              this.checkTorchAbove(x, y, z);
              this.dropFurnaceContents(x, y, z);
              this.dropChestContents(x, y, z);

              // Authentic ~33% drop chance for destroyed blocks in survival
              if (this.mode === "survival" && Math.random() < 0.33) {
                const def = BLOCKS[block];
                if (def && def.drop > 0) {
                  this.droppedItems.spawn(makeStack(def.drop), x + 0.5, y + 0.5, z + 0.5);
                }
              }
            }
          }
        }
      }
    }

    // 4. Entity damage & knockback
    const entityRadius = power * 2.0;

    // Player damage
    const playerDist = this.player.position.distanceTo(origin);
    if (playerDist <= entityRadius) {
      const impact = 1.0 - playerDist / entityRadius;
      const rawDamage = Math.round(((impact * impact + impact) / 2.0) * 8.0 * power + 1.0);
      const armorDef = this.inventory.totalArmorDefense;
      const cause = source === "creeper" ? "was blown up by a Creeper" : "was blown up by TNT";
      this.player.hurt(rawDamage, cause, armorDef);
      const broke = this.inventory.damageArmor(rawDamage);
      if (broke) {
        sound.playToolBreak();
        this.hud.toast("Armor broke!");
      }

      // Radial knockback
      const pdir = this.player.position.clone().sub(origin);
      const plen = pdir.length() || 1;
      pdir.x /= plen;
      pdir.y /= plen;
      pdir.z /= plen;
      this.player.velocity.x += pdir.x * impact * 14.0;
      this.player.velocity.y += Math.max(3.0, pdir.y * impact * 10.0 + 3.0);
      this.player.velocity.z += pdir.z * impact * 14.0;
    }

    // Passive animals
    for (const animal of this.animals.animals) {
      const animalDist = animal.position.distanceTo(origin);
      if (animalDist <= entityRadius) {
        const impact = 1.0 - animalDist / entityRadius;
        const damage = Math.round(((impact * impact + impact) / 2.0) * 8.0 * power + 1.0);
        this.animals.damage(animal, damage, origin);
      }
    }

    // Other hostiles
    for (const hostile of this.hostiles.hostiles) {
      const hostileDist = hostile.position.distanceTo(origin);
      if (hostileDist <= entityRadius) {
        const impact = 1.0 - hostileDist / entityRadius;
        const damage = Math.round(((impact * impact + impact) / 2.0) * 8.0 * power + 1.0);
        this.hostiles.damage(hostile, damage, origin);
      }
    }
  }

  private updateDebug(dt: number, frameStart: number, light: number): void {
    this.frameTimes.push(dt * 1000);
    if (this.frameTimes.length > 60) this.frameTimes.shift();
    const avg = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    const cpu = performance.now() - frameStart;

    const p = this.player.position;
    const cx = Math.floor(p.x / CHUNK_X);
    const cz = Math.floor(p.z / CHUNK_X);
    const biome = BIOME_NAMES[this.world.generator.columnAt(Math.floor(p.x), Math.floor(p.z)).biome];
    const hit = this.currentHit();
    const s = this.world.stats;
    const vitals =
      this.mode === "survival"
        ? `   health ${(this.player.health / 2).toFixed(1)}/10  air ${this.player.air.toFixed(1)}/10`
        : "";

    const lines = [
      `VoxelCraft   ${(1000 / Math.max(avg, 0.01)).toFixed(0)} fps  (${avg.toFixed(1)} ms frame)`,
      `cpu          ${cpu.toFixed(2)} ms`,
      `xyz          ${p.x.toFixed(2)}  ${p.y.toFixed(2)}  ${p.z.toFixed(2)}`,
      `chunk        ${cx}, ${cz}   biome ${biome}`,
      `time         ${formatClock(this.engine.sky.time)}   light ${(light * 100).toFixed(0)}%`,
      `state        ${this.player.flying ? "flying" : this.player.onGround ? "grounded" : "airborne"}${this.player.inWater ? " · in water" : ""}`,
      `mode         ${MODE_LABELS[this.mode]}${vitals}`,
      `seed         ${this.world.seed}`,
      `chunks       ${s.chunksLoaded} loaded  (+${s.genThisFrame} gen, ${s.meshBuildsThisFrame} meshed this frame)`,
      `meshes       ${s.chunksMeshed} built total`,
      `drops        ${this.droppedItems.items.length} in world`,
      `animals      ${this.animals.animals.length} in world`,
      `hostiles     ${this.hostiles.hostiles.length} in world (${this.engine.sky.isNight ? "night" : "day"}) · ${this.tntSimulation.tnts.length} primed tnt`,
      `triangles    ${this.engine.renderer.info.render.triangles.toLocaleString()}`,
      `looking at   ${hit ? `${blockName(hit.block)} @ ${hit.x} ${hit.y} ${hit.z}` : "—"}`,
    ];
    this.hud.setDebug(lines);
  }
}

function clampInt(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(value)));
}

function formatClock(t: number): string {
  const totalMinutes = Math.floor(t * 24 * 60);
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
